import { 
    ChangeDetectionStrategy, 
    ChangeDetectorRef, 
    Component, 
    Inject, 
    OnDestroy, 
    OnInit, 
    ViewChild, 
    ViewEncapsulation 
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuditHistory, AuditHistoryInput } from '../../models/audit-history.model';
import { ReportHistoryService } from '../../services/report-history.service';
import * as moment from 'moment-timezone';

/**
 * Report History Component
 * Displays audit/history data in a popup dialog
 * Reusable from any part of the application
 */
@Component({
    selector: 'app-report-history',
    templateUrl: './report-history.component.html',
    styleUrls: ['./report-history.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportHistoryComponent implements OnInit, OnDestroy {
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    // Table configuration
    displayedColumns: string[] = ['propertyName', 'oldValue', 'newValue', 'changedByName', 'changedAt'];
    dataSource: MatTableDataSource<AuditHistory>;

    // State management
    isLoading = true;
    hasError = false;
    errorMessage = '';

    // Constants
    readonly PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
    readonly DEFAULT_PAGE_SIZE = 10;

    private _unsubscribeAll: Subject<void> = new Subject<void>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _reportHistoryService: ReportHistoryService,
        private _matDialogRef: MatDialogRef<ReportHistoryComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AuditHistoryInput
    ) {
        this.dataSource = new MatTableDataSource<AuditHistory>([]);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Validate input data
        if (!this.data || !this.data.entityId || !this.data.entityType) {
            this.hasError = true;
            this.errorMessage = 'Invalid data provided. Entity ID and Type are required.';
            this.isLoading = false;
            this._changeDetectorRef.markForCheck();
            return;
        }

        // Fetch audit history
        this.loadAuditHistory();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    /**
     * After view init
     */
    ngAfterViewInit(): void {
        if (this.sort && this.paginator) {
            this.dataSource.sort = this.sort;
            this.dataSource.paginator = this.paginator;
            this._changeDetectorRef.markForCheck();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Load audit history from service
     */
    loadAuditHistory(): void {
        this.isLoading = true;
        this.hasError = false;
        this._changeDetectorRef.markForCheck();

        this._reportHistoryService
            .getAuditHistory(this.data)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (history: AuditHistory[]) => {
                    this.dataSource.data = history;
                    this.isLoading = false;
                    this._changeDetectorRef.markForCheck();
                },
                error: (error) => {
                    console.error('Failed to load audit history:', error);
                    this.hasError = true;
                    this.errorMessage = error.message || 'Failed to load audit history. Please try again.';
                    this.isLoading = false;
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    /**
     * Close the dialog
     */
    close(): void {
        this._matDialogRef.close();
    }

    /**
     * Get the dialog title based on entity type
     */
    getDialogTitle(): string {
        return `${this.data.entityType} History`;
    }

    /**
     * Check if data is empty
     */
    isDataEmpty(): boolean {
        return !this.isLoading && !this.hasError && this.dataSource.data.length === 0;
    }

    /**
     * Format date for display - Convert UTC to local timezone
     */
    formatDate(date: Date | string): string {
        if (!date) {
            return '-';
        }
        
        // Explicitly parse the date as UTC (append 'Z' if not present)
        let dateStr = typeof date === 'string' ? date : date.toISOString();
        if (!dateStr.endsWith('Z') && !dateStr.includes('+')) {
            dateStr += 'Z'; // Ensure it's treated as UTC
        }
        
        const utcDate = moment.utc(dateStr);
        
        if (!utcDate.isValid()) {
            return '-';
        }
        
        // Convert to user's local timezone
        const localDate = utcDate.local();
        return localDate.format('DD/MM/YYYY hh:mm A');
    }

    /**
     * Format value for display (handle null/undefined)
     */
    formatValue(value: string | null): string {
        return value ?? '-';
    }
}
